import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDebounce } from 'react-use'
import toast from 'react-hot-toast'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Box, Button, FormControlLabel, MenuItem, Switch, TextField, Typography } from '@mui/material'
import translate from '../../i18n/translate'
import { formatOtpDate } from '../UI/OtpPhonesPage'
import { getApiErrorMessage, RobotSmsDeclaration, RobotSmsDeclarationStatus, robotSmsPolicyApi } from '../../modules/services/robotSmsPolicyService'
import DeclarationDetailsDialog from './DeclarationDetailsDialog'
import DeclarationStatusChip from './DeclarationStatusChip'
import { gridSx } from './utils'
import useStyles from './useStyles'

const STATUSES: RobotSmsDeclarationStatus[] = ['pending', 'approved', 'rejected', 'revoked']

// Who signed the declaration and when, with the filled details. Approve / reject / revoke happens in the details dialog.
const DeclarationsTab = () => {
  const intl = useIntl()
  const classes = useStyles()

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [status, setStatus] = useState<RobotSmsDeclarationStatus | ''>('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)

  useDebounce(() => {
    setSearch(searchInput.trim())
    setPage(0)
  }, 400, [searchInput])

  const { data, isFetching, refetch } = robotSmsPolicyApi.useGetRobotSmsDeclarationsQuery({
    skip: page * pageSize,
    take: pageSize,
    search: search || undefined,
    status: status || undefined,
  })
  const { data: settings } = robotSmsPolicyApi.useGetRobotSmsSettingsQuery()
  const [updateSettings, { isLoading: isUpdatingSettings }] = robotSmsPolicyApi.useUpdateRobotSmsSettingsMutation()

  const selected = data?.list.find((item) => item.id === selectedId) ?? null

  const onAutoApproveChange = async (autoApproveDeclarations: boolean) => {
    try {
      await updateSettings({ autoApproveDeclarations }).unwrap()
      toast.success(intl.formatMessage({ id: autoApproveDeclarations ? 'robotSmsAutoApproveOn' : 'robotSmsAutoApproveOff' }))
    } catch (error) {
      toast.error(getApiErrorMessage(error) || intl.formatMessage({ id: 'robotSmsPolicyUpdateFailed' }))
    }
  }

  // File links are signed for 10 minutes - reload them whenever a declaration is opened
  const openDetails = (id: number) => {
    setSelectedId(id)
    refetch()
  }

  const columns = useMemo<GridColDef<RobotSmsDeclaration>[]>(() => [
    {
      field: 'acceptedAt',
      flex: 0.9,
      sortable: false,
      headerName: intl.formatMessage({ id: 'robotSmsDeclarationSignedAt' }),
      renderCell: ({ row }) => formatOtpDate(row.acceptedAt),
    },
    {
      field: 'reviewStatus',
      flex: 0.8,
      sortable: false,
      headerName: intl.formatMessage({ id: 'robotSmsPolicyStatus' }),
      renderCell: ({ row }) => <DeclarationStatusChip status={row.reviewStatus} />,
    },
    {
      field: 'declarantFullName',
      flex: 1.2,
      sortable: false,
      headerName: intl.formatMessage({ id: 'robotSmsDeclarant' }),
      renderCell: ({ row }) => (
        <Box className={classes.Cell}>
          <Typography>{row.declarantFullName}</Typography>
          <Typography variant="body2" color="text.secondary">{row.declarantIdNumber}</Typography>
        </Box>
      ),
    },
    {
      field: 'businessName',
      flex: 1.2,
      sortable: false,
      headerName: intl.formatMessage({ id: 'robotSmsDeclarationBusiness' }),
      renderCell: ({ row }) => (
        <Box className={classes.Cell}>
          <Typography>{row.businessName}</Typography>
          <Typography variant="body2" color="text.secondary">{row.businessRegistrationNumber}</Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      flex: 1.2,
      sortable: false,
      headerName: intl.formatMessage({ id: 'robotSmsDeclarationContacts' }),
      renderCell: ({ row }) => (
        <Box className={classes.Cell}>
          <Typography className={classes.FileName}>{row.email}</Typography>
          <Typography variant="body2" color="text.secondary">{row.phone}</Typography>
        </Box>
      ),
    },
    {
      field: 'policyVersion',
      flex: 0.5,
      sortable: false,
      headerName: intl.formatMessage({ id: 'robotSmsPolicyVersion' }),
    },
    {
      field: 'actions',
      flex: 0.7,
      sortable: false,
      headerName: intl.formatMessage({ id: 'robotSmsPolicyActions' }),
      renderCell: ({ row }) => (
        <Button color="warning" variant={row.reviewStatus === 'pending' ? 'contained' : 'text'} size="small" onClick={() => openDetails(row.id)}>
          {translate(row.reviewStatus === 'pending' ? 'robotSmsDeclarationReview' : 'robotSmsDeclarationOpen')}
        </Button>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [intl, classes])

  return (
    <>
      <Box className={classes.SettingsCard}>
        <FormControlLabel
          control={
            <Switch
              color="warning"
              checked={settings?.autoApproveDeclarations ?? true}
              disabled={!settings || isUpdatingSettings}
              onChange={(e) => onAutoApproveChange(e.target.checked)}
            />
          }
          label={<Typography fontWeight={600}>{translate('robotSmsAutoApprove')}</Typography>}
        />
        <Typography variant="body2" color="text.secondary">
          {translate(settings?.autoApproveDeclarations === false ? 'robotSmsAutoApproveOffHint' : 'robotSmsAutoApproveOnHint')}
        </Typography>
      </Box>

      <div className={classes.TabToolbar}>
        <TextField
          select
          size="small"
          color="warning"
          label={intl.formatMessage({ id: 'robotSmsPolicyStatus' })}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as RobotSmsDeclarationStatus | '')
            setPage(0)
          }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{translate('robotSmsDeclarationStatusAll')}</MenuItem>
          {STATUSES.map((item) => (
            <MenuItem key={item} value={item}>
              {translate(`robotSmsDeclarationStatus_${item}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          color="warning"
          label={intl.formatMessage({ id: 'robotSmsDeclarationSearch' })}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 320 }}
        />
      </div>

      <DataGrid
        sx={gridSx}
        rows={data?.list || []}
        rowCount={data?.count || 0}
        columns={columns}
        loading={isFetching}
        getRowHeight={() => 'auto'}
        disableSelectionOnClick
        pagination
        paginationMode="server"
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize)
          setPage(0)
        }}
        rowsPerPageOptions={[20, 50, 100]}
        autoHeight
        density="comfortable"
      />

      {selected && <DeclarationDetailsDialog declaration={selected} onClose={() => setSelectedId(null)} />}
    </>
  )
}

export default DeclarationsTab
